/**
 * Common items in the Signup and Profile form
 */
import React, { useCallback, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Avatar,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Cake as CakeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { CountrySelect, LanguageSelect } from "@user27828/shared-utils/client";
import {
  ethnicityOptions,
  genderOptions,
  pronounOptions,
  raceOptions,
  sexualOrientationOptions,
} from "@smfa/utils/src/data/demographic-options";
import { uniq } from "lodash";

// Base required fields - anything added to UserProfileForm({required}) will be added to this list
export const requiredFields = ["first_name", "last_name", "email_id"];

/**
 * @param {Object} props - The component props
 * @param {string} props.usage - The usage of the form (e.g., "profile", "signup")
 * @param {Object} props.formData - The form data
 * @param {Function} props.handle* - Various handlers
 * @param {boolean} props.showDemographicBlurb - Flag to show demographic blurb
 * @param {JSX.element} props.prepend - Prepend element
 * @param {JSX.element} props.append - Append element
 * @param {Array} props.required - Required fields example: ["first_name", "last_name"] - triggers handling for potential validation if present
 * @returns {JSX.element}
 * @component
 */
const UserProfileForm = ({
  usage = "profile",
  formData,
  handleInputChange,
  handleDateChange,
  handlePhoneChange,
  handleCountryChange,
  handleLanguageChange,
  validationErrors,
  showDemographicBlurb = false,
  prepend = null,
  append = null,
  required = [],
}) => {
  const _required = uniq([...requiredFields, ...required]); // Combine base and user-specified required fields
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmFieldTouched, setConfirmFieldTouched] = useState(false);

  // toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  // Validate password - update to detect mismatches immediately when both fields are filled
  const validatePassword = useCallback(
    (newPassword = password, newConfirmPassword = confirmPassword) => {
      // This function's job is to set validationErrors.password.
      // Accept parameters to validate with the most current values

      if (!(validationErrors && typeof validationErrors === "object")) {
        // Cannot set errors if validationErrors prop is not a valid object.
        return true; // Assuming valid if cannot set error state.
      }

      // Check length
      if (newPassword && newPassword.length < 8) {
        validationErrors.password =
          "Password must be at least 8 characters long";
        return false; // Validation fails
      }

      // Check for mismatch if confirmPassword has a value
      if (
        newPassword &&
        newConfirmPassword &&
        newPassword !== newConfirmPassword
      ) {
        validationErrors.password = "Passwords do not match";
        return false; // Validation fails
      }

      // If all checks passed or are not applicable (e.g., confirmPassword is empty but password length is fine)
      validationErrors.password = null;
      return true; // Validation passes or no relevant errors
    },
    [password, confirmPassword, validationErrors],
  );

  // Handle confirm password change to validate in real-time
  const handleConfirmPasswordChange = useCallback(
    (e) => {
      // Prevent browser extensions (like iCloud Passwords) from interfering
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      const newValue = e.target.value;
      setConfirmPassword(newValue);

      // Validate immediately with the new confirm password value
      validatePassword(password, newValue);
    },
    [validatePassword, password], // Include password in dependencies
  );

  // function to handle confirm password field blur
  const handleConfirmPasswordBlur = useCallback(() => {
    setConfirmFieldTouched(true);
    validatePassword(password, confirmPassword);
  }, [validatePassword, password, confirmPassword]);

  // Enhanced password change handler to prevent extension interference
  const handlePasswordChange = useCallback(
    (e) => {
      // Prevent browser extensions (like iCloud Passwords) from interfering
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      const newPasswordValue = e.target.value;
      setPassword(newPasswordValue); // Update local state

      // Directly update parent's formData.password prop
      if (formData && typeof formData === "object" && "password" in formData) {
        formData.password = newPasswordValue;
      }

      // Validate immediately with the new password value
      validatePassword(newPasswordValue, confirmPassword);
    },
    [formData, confirmPassword, validatePassword],
  );

  // Enhanced form input handler to prevent extension interference
  const handleFormInputChange = useCallback(
    (e) => {
      // Prevent browser extensions from interfering with form events
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      // Call the original handler
      if (handleInputChange) {
        handleInputChange(e);
      }
    },
    [handleInputChange],
  );

  return (
    <>
      {prepend}

      {/* Personal Information Section */}
      <Typography
        variant="h6"
        component="h2"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", mb: 2 }}
      >
        <Avatar sx={{ width: 24, height: 24, mr: 1 }} variant="square">
          <PersonIcon />
        </Avatar>
        Personal Information
      </Typography>
      <Grid container spacing={3} mb={4}>
        {/* Title field - Added to match onlineapplication.jsx */}
        <Grid size={{ xs: 12, sm: 2 }}>
          <FormControl fullWidth required={_required.includes("title")}>
            <InputLabel id="title-label">Title</InputLabel>
            <Select
              labelId="title-label"
              name="title"
              value={formData.title || ""}
              label={`Title`}
              error={
                _required.includes("title") ? !!validationErrors?.title : null
              }
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="Dr.">Dr.</MenuItem>
              <MenuItem value="Mr.">Mr.</MenuItem>
              <MenuItem value="Mrs.">Mrs.</MenuItem>
              <MenuItem value="Ms.">Ms.</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {/* First Name */}
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="First Name"
            name="first_name"
            value={formData.first_name || ""}
            onChange={handleInputChange}
            fullWidth
            required={_required.includes("first_name")}
            error={
              _required.includes("first_name")
                ? !!validationErrors.first_name
                : null
            }
            helperText={
              _required.includes("first_name")
                ? validationErrors.first_name
                : null
            }
          />
        </Grid>
        {/* Middle Name */}
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            label="Middle Name"
            name="middle_name"
            value={formData.middle_name || ""}
            onChange={handleInputChange}
            fullWidth
            required={_required.includes("middle_name")}
            error={
              _required.includes("middle_name")
                ? !!validationErrors.middle_name
                : null
            }
            helperText={
              _required.includes("middle_name")
                ? validationErrors.middle_name
                : null
            }
          />
        </Grid>
        {/* Last Name */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Last Name"
            name="last_name"
            value={formData.last_name || ""}
            onChange={handleInputChange}
            fullWidth
            required={_required.includes("last_name")}
            error={
              _required.includes("last_name")
                ? !!validationErrors.last_name
                : null
            }
            helperText={
              _required.includes("last_name")
                ? validationErrors.last_name
                : null
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Email"
            name="email_id"
            value={formData.email_id || ""}
            onChange={handleFormInputChange}
            onKeyDown={(e) => {
              // Prevent browser extensions from interfering with keyboard events
              if (e && typeof e.stopPropagation === "function") {
                e.stopPropagation();
              }
            }}
            fullWidth
            required={_required.includes("email_id")}
            type="email"
            error={!!validationErrors.email_id}
            helperText={
              validationErrors.email_id ||
              (usage === "profile"
                ? "Changing email will require you to log in again"
                : "")
            }
            slotProps={{
              input: {
                // Add data attributes to help extensions identify this properly
                "data-form-type": usage,
                "data-field-type": "email",
                startAdornment: (
                  <EmailIcon sx={{ color: "action.active", mr: 1 }} />
                ),
              },
            }}
          />
        </Grid>
        {/* Date of Birth */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of Birth"
              value={formData.dob ? dayjs(formData.dob) : null}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: _required.includes("dob"),
                  error: _required.includes("dob")
                    ? !!validationErrors.dob
                    : null,
                  helperText: validationErrors.dob || "",
                  slotProps: {
                    input: {
                      startAdornment: (
                        <CakeIcon sx={{ color: "action.active", mr: 1 }} />
                      ),
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        {usage === "signup" && (
          <Grid container spacing={2} size={12}>
            {" "}
            {/* Added spacing={2} */}
            {/* Password Field - Editable */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                id="password"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={(e) => {
                  // Prevent browser extensions from interfering with keyboard events
                  if (e && typeof e.stopPropagation === "function") {
                    e.stopPropagation();
                  }
                }}
                fullWidth
                margin="dense"
                variant="outlined"
                autoComplete="new-password"
                error={!!validationErrors?.password} // Added error prop
                helperText={validationErrors?.password || ""}
                slotProps={{
                  input: {
                    // Add data attributes to help extensions identify this as a registration password
                    "data-form-type": "signup",
                    "data-password-type": "new",
                    endAdornment: (
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        onMouseDown={(e) => {
                          // Prevent extensions from interfering with click events
                          if (e && typeof e.stopPropagation === "function") {
                            e.stopPropagation();
                          }
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    ),
                  },
                }}
              />
            </Grid>
            {/* Confirm Password Field - Editable */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                name="confirm_account_password"
                id="confirm_account_password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={handleConfirmPasswordBlur}
                onKeyDown={(e) => {
                  // Prevent browser extensions from interfering with keyboard events
                  if (e && typeof e.stopPropagation === "function") {
                    e.stopPropagation();
                  }
                }}
                fullWidth
                margin="dense"
                variant="outlined"
                autoComplete="new-password"
                error={Boolean(validationErrors.password)}
                helperText={validationErrors.password || " "}
                slotProps={{
                  input: {
                    // Add data attributes to help extensions identify this as a confirmation password
                    "data-form-type": "signup",
                    "data-password-type": "confirm",
                  },
                }}
              />
            </Grid>
          </Grid>
        )}
        {/* Phone Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ mb: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Phone Number
            </Typography>
            <PhoneInput
              international
              defaultCountry="US"
              value={
                formData.phone_number && formData.phone_number.startsWith("+")
                  ? formData.phone_number
                  : ""
              }
              onChange={handlePhoneChange}
              required={_required.includes("phone_number")}
              className={validationErrors.phone_number ? "error" : ""}
              inputComponent="input"
              style={{
                width: "100%",
                height: "56px", // Match MUI TextField height
              }}
            />
            {validationErrors.phone_number && (
              <Typography variant="caption" color="error">
                {validationErrors.phone_number}
              </Typography>
            )}
          </Box>
        </Grid>
        {/* Country */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <CountrySelect
              id="country"
              name="country"
              label="Country"
              value={formData.country || ""}
              topCountries={["US"]}
              onChange={handleCountryChange}
              fullWidth
              searchable={true}
              variant="outlined"
              required={_required.includes("country")}
              error={!!validationErrors.country}
              helperText={validationErrors.country || ""}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          variant="outlined"
          label="Address"
          name="address"
          value={formData.address || ""}
          onChange={handleInputChange}
          fullWidth
          multiline
          rows={3}
          required={_required.includes("address")}
          error={!!validationErrors.address}
          helperText={validationErrors.address || ""}
          slotProps={{
            input: {
              startAdornment: (
                <LocationIcon sx={{ color: "action.active", mr: 1 }} />
              ),
            },
          }}
        />
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Professional Information Section */}
      <Typography variant="h6" component="h2" gutterBottom>
        Professional Information
      </Typography>
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Institution"
            name="institution"
            value={formData.institution || ""}
            onChange={handleInputChange}
            fullWidth
            required={_required.includes("institution")}
            error={!!validationErrors.institution}
            helperText={validationErrors.institution || ""}
            slotProps={{
              input: {
                startAdornment: (
                  <SchoolIcon sx={{ color: "action.active", mr: 1 }} />
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required={_required.includes("profession")}>
            <InputLabel id="profession-label">Profession</InputLabel>
            <Select
              labelId="profession-label"
              name="profession"
              value={formData.profession || ""}
              label="Profession"
              onChange={handleInputChange}
              error={!!validationErrors.profession}
              startAdornment={
                <WorkIcon sx={{ color: "action.active", mr: 1 }} />
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="Physician">Physician</MenuItem>
              <MenuItem value="CRNA">CRNA</MenuItem>
              <MenuItem value="AA">AA</MenuItem>
              <MenuItem value="Trainee">Trainee</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Specialty"
            name="specialty"
            value={formData.specialty || ""}
            onChange={handleInputChange}
            fullWidth
            required={_required.includes("specialty")}
            error={!!validationErrors.specialty}
            helperText={validationErrors.specialty || ""}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Subspecialty"
            name="subspecialty"
            value={formData.subspecialty || ""}
            onChange={handleInputChange}
            fullWidth
            required={_required.includes("subspecialty")}
            error={!!validationErrors.subspecialty}
            helperText={validationErrors.subspecialty || ""}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <LanguageSelect
              id="language"
              name="language"
              label="Language(s)"
              value={formData.language || []}
              topLanguages={["EN", "ES", "FR", "ZH"]}
              onChange={handleLanguageChange}
              multiple={true}
              fullWidth
              searchable={true}
              variant="outlined"
              required={_required.includes("language")}
              error={!!validationErrors.language}
              helperText={validationErrors.language || ""}
            />
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Additional Information Section */}
      <Typography variant="h6" component="h2" gutterBottom>
        Additional Options
      </Typography>

      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12 }}>
          <FormControl fullWidth>
            <FormControlLabel
              control={
                <Checkbox
                  name="directory_show"
                  checked={formData.directory_show !== 0}
                  onChange={(e) =>
                    handleInputChange({
                      target: {
                        name: "directory_show",
                        value: e.target.checked ? 1 : 0,
                      },
                    })
                  }
                />
              }
              label="Allow my information to be in the member directory"
            />
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Demographic Information Section */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Demographic Information
        </Typography>
        {showDemographicBlurb && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              component="paragraph"
            >
              To help us accurately assess the diversity of the society, please
              complete the following demographic information. Confidential
              demographic data will be used in the aggregate and only for
              authorized purposes. Your participation is voluntary but would be
              appreciated; use "prefer not to answer" to decline. Please select
              all options that apply.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              component="paragraph"
            >
              Why are we asking? By knowing more about the diversity of our
              membership, SMFA can more effectively develop resources and
              programming to serve member needs, which in turn will benefit the
              patient population we serve.
            </Typography>
          </>
        )}
      </Box>
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="gender-label">Gender Identity</InputLabel>
            <Select
              labelId="gender-label"
              name="gender"
              value={formData.gender || ""}
              label="Gender Identity"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {genderOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="pronouns-label">Pronouns</InputLabel>
            <Select
              labelId="pronouns-label"
              name="pronouns"
              value={formData.pronouns || ""}
              label="Pronouns"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {pronounOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="race-label">Race</InputLabel>
            <Select
              labelId="race-label"
              name="race"
              value={formData.race || ""}
              label="Race"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {raceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth>
            <InputLabel id="ethnicity-label">Ethnicity</InputLabel>
            <Select
              labelId="ethnicity-label"
              name="ethnicity"
              value={formData.ethnicity || ""}
              label="Ethnicity"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {ethnicityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormControl fullWidth>
            <InputLabel id="sexual-orientation-label">
              Sexual Orientation
            </InputLabel>
            <Select
              labelId="sexual-orientation-label"
              name="sexual_orientation"
              value={formData.sexual_orientation || ""}
              label="Sexual Orientation"
              onChange={handleInputChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sexualOrientationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {append}
    </>
  );
};

export default UserProfileForm;
